import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthProvider';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiGrid,
  FiUsers,
  FiHome,
  FiSettings,
  FiFileText,
  FiLogOut,
  FiShield,
  FiCreditCard,
  FiActivity,
  FiMenu,
  FiX,
  FiChevronDown
} = FiIcons;

/**
 * PlatformAdminLayout - Main layout for the platform admin panel
 * Provides navigation sidebar and header for superadmin operations
 */
const PlatformAdminLayout = () => {
  const { user, superadminRole, hasSuperadminPermission, signOut } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Navigation items with permission checks
  const navItems = [
    {
      name: 'Dashboard',
      path: '/platform-admin',
      icon: FiGrid,
      permission: null, // All superadmins can access
      end: true
    },
    {
      name: 'Metrics',
      path: '/platform-admin/metrics',
      icon: FiActivity,
      permission: 'metrics:read'
    },
    {
      name: 'Tenants',
      path: '/platform-admin/tenants',
      icon: FiHome,
      permission: 'tenants:read'
    },
    {
      name: 'Users',
      path: '/platform-admin/users',
      icon: FiUsers,
      permission: 'users:read'
    },
    {
      name: 'Subscriptions',
      path: '/platform-admin/subscriptions',
      icon: FiCreditCard,
      permission: 'subscriptions:read'
    },
    {
      name: 'Audit Log',
      path: '/platform-admin/audit',
      icon: FiFileText,
      permission: 'audit:read'
    },
    {
      name: 'MFA Setup',
      path: '/platform-admin/mfa',
      icon: FiShield,
      permission: null // All superadmins can access
    }
  ];

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter(item => 
    !item.permission || hasSuperadminPermission(item.permission)
  );

  // Role display names
  const roleDisplayNames = {
    platform_owner: 'Platform Owner',
    platform_admin: 'Platform Admin',
    subscription_admin: 'Subscription Admin',
    support_admin: 'Support Admin'
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Header */}
      <header className="bg-indigo-900 text-white shadow-lg fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-indigo-800 transition-colors"
            >
              <SafeIcon icon={sidebarOpen ? FiX : FiMenu} className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-indigo-300" />
              <span className="font-bold text-lg">Platform Admin</span>
            </div>
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-indigo-800 transition-colors"
            >
              <div className="w-8 h-8 bg-indigo-700 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium">{user?.email}</p>
                <p className="text-xs text-indigo-300">
                  {roleDisplayNames[superadminRole] || superadminRole}
                </p>
              </div>
              <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  <p className="text-xs text-gray-500">
                    {roleDisplayNames[superadminRole] || superadminRole}
                  </p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-14 bottom-0 bg-white shadow-lg transition-all duration-300 z-40 ${
          sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'
        }`}
      >
        <nav className="p-4 space-y-1">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                }`
              }
            >
              <SafeIcon icon={item.icon} className="w-5 h-5" />
              <span>{item.name}</span>
            </NavLink>
          ))}
        </nav>

        {/* Role Badge */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-indigo-50 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiShield} className="w-4 h-4 text-indigo-600" />
              <span className="text-xs font-medium text-indigo-700">
                {roleDisplayNames[superadminRole] || 'Superadmin'}
              </span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`pt-14 transition-all duration-300 ${
          sidebarOpen ? 'ml-64' : 'ml-0'
        }`}
      >
        <div className="p-6">
          <Outlet />
        </div>
      </main>

      {/* Click outside to close user menu */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default PlatformAdminLayout;
